import { useEffect, useState } from 'react';
import { LottieSparkle } from '../shared/LottieSparkle';

// Individual badge PNGs (256×256, cropped from sprite)
// Available at /badges/{name}.png in each app's public folder.

export type BadgeType =
  | 'fam'
  | 'approved'
  | 'funded-circle'
  | 'funded-square'
  | 'angel-investor'
  | 'authorized-dealer';

const BADGE_SRC: Record<BadgeType, string> = {
  'fam':               '/badges/fam.png',
  'approved':          '/badges/approved.png',
  'funded-circle':     '/badges/funded-circle.png',
  'funded-square':     '/badges/funded-square.png',
  'angel-investor':    '/badges/angel-investor.png',
  'authorized-dealer': '/badges/authorized-dealer.png',
};

const BADGE_LABEL: Record<BadgeType, string> = {
  'fam':               'FAM',
  'approved':          'Approved',
  'funded-circle':     'Funded',
  'funded-square':     'Funded',
  'angel-investor':    'Angel Investor',
  'authorized-dealer': 'Authorized Dealer',
};

interface BadgeProps {
  type: BadgeType;
  size?: number;        // display px, default 64
  animate?: 'pop' | 'pulse' | 'spin' | 'none';
  className?: string;
}

export function Badge({ type, size = 64, animate = 'none', className = '' }: BadgeProps) {
  const animStyle: React.CSSProperties =
    animate === 'spin'  ? { animation: 'fam-badge-spin 1.4s linear infinite' } :
    animate === 'pulse' ? { animation: 'fam-badge-pulse 1.8s ease-in-out infinite' } :
    animate === 'pop'   ? { animation: 'fam-badge-pop 0.4s cubic-bezier(0.34,1.56,0.64,1) both' } :
    {};

  return (
    <>
      <style>{`
        @keyframes fam-badge-spin  { to { transform: rotate(360deg); } }
        @keyframes fam-badge-pulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:.75; transform:scale(.92); } }
        @keyframes fam-badge-pop   { from { opacity:0; transform:scale(.4); } to { opacity:1; transform:scale(1); } }
      `}</style>
      <img
        src={BADGE_SRC[type]}
        alt={BADGE_LABEL[type]}
        width={size}
        height={size}
        className={`inline-block flex-shrink-0 ${className}`}
        style={{ width: size, height: size, objectFit: 'contain', ...animStyle }}
      />
    </>
  );
}

// ─── FAM Spinner (loading state) ──────────────────────────────────────────────
// Replaces generic spinners throughout the app with the FAM badge.

interface FamSpinnerProps {
  size?: number;
  label?: string;
  className?: string;
}

export function FamSpinner({ size = 48, label, className = '' }: FamSpinnerProps) {
  return (
    <div className={`flex flex-col items-center justify-center gap-3 ${className}`}>
      <Badge type="fam" size={size} animate="pulse" />
      {label && <p className="text-sm text-gray-500 font-medium">{label}</p>}
    </div>
  );
}

// ─── Notification toast ────────────────────────────────────────────────────

interface BadgeNotificationProps {
  type: BadgeType;
  title: string;
  description?: string;
  onDismiss?: () => void;
  autoDismissMs?: number;
}

export function BadgeNotification({
  type,
  title,
  description,
  onDismiss,
  autoDismissMs = 4000,
}: BadgeNotificationProps) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onDismiss?.(), 400);
    }, autoDismissMs);
    return () => clearTimeout(t);
  }, [autoDismissMs, onDismiss]);

  return (
    <div
      className={`flex items-center gap-3 bg-white border border-gray-200 rounded-2xl shadow-xl px-4 py-3 pointer-events-auto transition-all duration-400 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
      }`}
      style={{ minWidth: 260, maxWidth: 340 }}
    >
      <div className="relative flex-shrink-0">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ width: 80, height: 80, top: -14, left: -14 }}>
          <LottieSparkle />
        </div>
        <Badge type={type} size={52} animate="pop" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-gray-900 text-sm leading-tight">{title}</p>
        {description && (
          <p className="text-xs text-gray-500 mt-0.5 leading-snug">{description}</p>
        )}
      </div>
      {onDismiss && (
        <button
          onClick={() => { setVisible(false); setTimeout(() => onDismiss(), 400); }}
          className="text-gray-300 hover:text-gray-500 transition-colors flex-shrink-0 text-lg leading-none"
        >
          ×
        </button>
      )}
    </div>
  );
}

// ─── Notification stack (portal-style, fixed top-right) ───────────────────

export interface BadgeNotificationItem {
  id: string;
  type: BadgeType;
  title: string;
  description?: string;
}

interface BadgeNotificationStackProps {
  items: BadgeNotificationItem[];
  onDismiss: (id: string) => void;
}

export function BadgeNotificationStack({ items, onDismiss }: BadgeNotificationStackProps) {
  if (!items.length) return null;
  return (
    <div className="fixed top-20 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
      {items.map((item) => (
        <BadgeNotification
          key={item.id}
          type={item.type}
          title={item.title}
          description={item.description}
          onDismiss={() => onDismiss(item.id)}
        />
      ))}
    </div>
  );
}
